import { PERMISSIONS, type Permission } from '../shared/constants/permissions';

export interface PermissionMetadata {
  description: string;
  action: string;
  scope: string;
}

export const PERMISSION_METADATA: Record<Permission, PermissionMetadata> = {
  [PERMISSIONS.TENDER_CREATE]: {
    description: 'Allows the user to create new tenders.',
    action: 'create new tenders',
    scope: 'under their account',
  },
  [PERMISSIONS.TENDER_VIEW]: {
    description: 'Allows the user to view tenders they are explicitly assigned to.',
    action: 'view tenders',
    scope: 'they are assigned to',
  },
  [PERMISSIONS.TENDER_VIEW_CLIENT]: {
    description: 'Allows the user to view all tenders across the client organization.',
    action: 'view all tenders',
    scope: 'across the client organization',
  },
  [PERMISSIONS.TENDER_FILES_VIEW_CLIENT]: {
    description:
      'Allows the user to view all files within a tender, overriding assignment restrictions.',
    action: 'view all files',
    scope: 'within accessible tenders',
  },
  [PERMISSIONS.TENDER_SECTIONS_VIEW_CLIENT]: {
    description:
      'Allows the user to view all sections within a tender, overriding assignment restrictions.',
    action: 'view all sections',
    scope: 'within accessible tenders',
  },
  [PERMISSIONS.TENDER_EDIT]: {
    description: 'Allows the user to modify tender details and settings.',
    action: 'edit tender details',
    scope: 'for accessible tenders',
  },
  [PERMISSIONS.TENDER_DELETE]: {
    description: 'Allows the user to permanently delete a tender.',
    action: 'delete tenders',
    scope: 'for accessible tenders',
  },
  [PERMISSIONS.TENDER_MANAGE_TEAM]: {
    description: 'Allows the user to manage team assignments and roles for a tender.',
    action: 'manage team assignments',
    scope: 'for accessible tenders',
  },
  [PERMISSIONS.TENDER_FULL_ACCESS]: {
    description: 'Allows the user full control and visibility within a specific tender.',
    action: 'full control',
    scope: 'within specific assigned tenders',
  },
  [PERMISSIONS.DRAFT_CREATE]: {
    description: 'Allows the user to create new document drafts.',
    action: 'create new drafts',
    scope: 'for accessible tenders',
  },
  [PERMISSIONS.DRAFT_EDIT]: {
    description: 'Allows the user to edit existing document drafts.',
    action: 'edit existing drafts',
    scope: 'for accessible tenders',
  },
  [PERMISSIONS.REVIEW_CREATE]: {
    description: 'Allows the user to initiate a review process for a draft.',
    action: 'initiate draft reviews',
    scope: 'for accessible tenders',
  },
  [PERMISSIONS.REPORT_CREATE]: {
    description: 'Allows the user to generate capability reports.',
    action: 'generate capability reports',
    scope: 'for accessible tenders',
  },
  [PERMISSIONS.INSIGHTS_VIEW]: {
    description: 'Allows the user to view the strategic insights dashboard.',
    action: 'view strategic insights',
    scope: 'within the client organization',
  },
  [PERMISSIONS.USER_INVITE]: {
    description: 'Allows the user to invite new users to the platform.',
    action: 'invite new users',
    scope: 'to the client organization',
  },
  [PERMISSIONS.USER_VIEW]: {
    description: 'Allows the user to view other user profiles.',
    action: 'view user profiles',
    scope: 'within the client organization',
  },
  [PERMISSIONS.USER_UPDATE]: {
    description: 'Allows the user to update other user profiles.',
    action: 'update user profiles',
    scope: 'within the client organization',
  },
  [PERMISSIONS.USER_DELETE]: {
    description: 'Allows the user to delete or deactivate user accounts.',
    action: 'delete or deactivate user accounts',
    scope: 'within the client organization',
  },
  [PERMISSIONS.USER_MANAGE_ROLES]: {
    description: 'Allows the user to manage user roles and specific permissions.',
    action: 'manage user roles and permissions',
    scope: 'within the client organization',
  },
  [PERMISSIONS.USER_MANAGE_LIMITS]: {
    description: 'Allows the user to manage individual user resource limits.',
    action: 'manage individual user resource limits',
    scope: 'within the client organization',
  },
  [PERMISSIONS.TEAM_CREATE]: {
    description: 'Allows the user to create new teams.',
    action: 'create new teams',
    scope: 'within the client organization',
  },
  [PERMISSIONS.TEAM_VIEW]: {
    description: 'Allows the user to view team structures and memberships.',
    action: 'view team structures',
    scope: 'within the client organization',
  },
  [PERMISSIONS.TEAM_UPDATE]: {
    description: 'Allows the user to update team settings and memberships.',
    action: 'update team settings',
    scope: 'within the client organization',
  },
  [PERMISSIONS.TEAM_DELETE]: {
    description: 'Allows the user to delete existing teams.',
    action: 'delete existing teams',
    scope: 'within the client organization',
  },
  [PERMISSIONS.TEAM_MANAGE_LIMITS]: {
    description: 'Allows the user to manage team resource limits.',
    action: 'manage team resource limits',
    scope: 'within the client organization',
  },
  [PERMISSIONS.CLIENT_PROFILE_UPDATE]: {
    description: 'Allows the user to update client organization profile details.',
    action: 'update organization profile details',
    scope: 'for the client organization',
  },
  [PERMISSIONS.CLIENT_VIEW]: {
    description: 'Allows the user to view client organization profile details.',
    action: 'view organization profile details',
    scope: 'for the client organization',
  },
  [PERMISSIONS.BILLING_MANAGE]: {
    description: 'Allows the user to manage subscription billing and invoices.',
    action: 'manage subscription billing',
    scope: 'for the client organization',
  },
  [PERMISSIONS.SETTINGS_MANAGE]: {
    description: 'Allows the user to manage global client organization settings.',
    action: 'manage global organization settings',
    scope: 'for the client organization',
  },
  [PERMISSIONS.TEMPLATE_VIEW_CLIENT]: {
    description: 'Allows the user to view document templates.',
    action: 'view document templates',
    scope: 'within their allowed scope',
  },
  [PERMISSIONS.TEMPLATE_CREATE]: {
    description: 'Allows the user to create new document templates.',
    action: 'create document templates',
    scope: 'within their allowed scope',
  },
  [PERMISSIONS.TEMPLATE_EDIT]: {
    description: 'Allows the user to edit existing document templates.',
    action: 'edit document templates',
    scope: 'within their allowed scope',
  },
  [PERMISSIONS.TEMPLATE_DELETE]: {
    description: 'Allows the user to delete document templates.',
    action: 'delete document templates',
    scope: 'within their allowed scope',
  },
  [PERMISSIONS.TEMPLATE_UPLOAD_TEAM]: {
    description: 'Allows the user to explicitly upload templates for their team.',
    action: 'upload team templates',
    scope: 'within their assigned teams',
  },
  [PERMISSIONS.TEMPLATE_MANAGE_CLIENT]: {
    description: 'Allows the user to manage company-wide document templates.',
    action: 'manage company templates',
    scope: 'across the client organization',
  },
  [PERMISSIONS.LIBRARY_VIEW_CLIENT]: {
    description: 'Allows the user to view the combined document library.',
    action: 'view the document library',
    scope: 'across personal, team, and company files',
  },
  [PERMISSIONS.LIBRARY_UPLOAD_TEAM]: {
    description: 'Allows the user to upload content and mark files as active in the Team library.',
    action: 'upload to the Team library',
    scope: 'for their assigned teams',
  },
  [PERMISSIONS.LIBRARY_DELETE]: {
    description: 'Allows the user to remove content from the Team library.',
    action: 'remove content from the Team library',
    scope: 'for their assigned teams',
  },
  [PERMISSIONS.LIBRARY_MANAGE_CLIENT]: {
    description:
      'Allows the user to upload, delete, and manage hidden files in the Company library.',
    action: 'manage the Company library',
    scope: 'across the client organization',
  },
  [PERMISSIONS.PUBLIC_TENDER_VIEW]: {
    description: 'Allows the user to view publicly available tenders.',
    action: 'view publicly available tenders',
    scope: 'across the platform',
  },
  [PERMISSIONS.PARTNER_VIEW]: {
    description: 'Allows the user to view the list and details of partners.',
    action: 'view partners',
    scope: 'across the platform',
  },
  [PERMISSIONS.PARTNER_MANAGE]: {
    description: 'Allows the user to manage relationships with partners (pre-approve, pre-reject).',
    action: 'manage partner relationships',
    scope: 'for the organization',
  },
  [PERMISSIONS.TICKET_CREATE]: {
    description: 'Allows the user to create support tickets.',
    action: 'create support tickets',
    scope: 'for their account',
  },
  [PERMISSIONS.TICKET_VIEW]: {
    description: 'Allows the user to view support tickets.',
    action: 'view support tickets',
    scope: 'for their account',
  },
  [PERMISSIONS.TICKET_UPDATE]: {
    description: 'Allows the user to update support tickets.',
    action: 'update support tickets',
    scope: 'for their account',
  },
  [PERMISSIONS.TICKET_MANAGE]: {
    description: 'Allows the user to manage support tickets.',
    action: 'manage support tickets',
    scope: 'for their account',
  },
  [PERMISSIONS.PROCUREMENT_ACCESS]: {
    description: 'Allows the user to access the Procurement Ecosystem workspaces.',
    action: 'access the procurement workspace',
    scope: 'within the client organization',
  },
};
